import React, { useState, useEffect } from "react";
import { Modal, Input, Switch, Row, Col, Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import RichTextEditor from "./RichTextEditor";
import { toast } from "react-toastify";

import ScheduleDatePicker from "./ScheduleDatePicker";
import ScheduleTimePicker from "./ScheduleTimePicker";
import ScheduleTimezonePicker from "./ScheduleTimezonePicker";
import { connect } from "react-redux";
import { sendInvite, updateShortlistedStatus } from "store/actions/tiers";

const RichTextEditorModal = ({
  open,
  onClose,
  initialHtml,
  initialSubject,
  selectedTalent,
  triggerSendInvite,
  htmlFooter = "",
  selectedRoleUUID,
  selectedRolePersonUUID,
  triggerUpdateShortlistedStatus,
  inviteType,
  token,
  shortlisted,
  inviteBtnName,
  emailMeta
}) => {
  const [subject, setSubject] = useState(initialSubject || "");
  const [html, setHtml] = useState(initialHtml);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [tz, setTz] = useState(undefined);
  useEffect(() => {
    setHtml(initialHtml);
  }, [initialHtml]);

  useEffect(() => {
    if (open && initialSubject && !subject) {
      setSubject(`Subject line: ${initialSubject}`);
    }
  }, [open, initialSubject]);

  useEffect(() => {
    if (!open) {
      setSubject("");
      setHtml("");
      setScheduleOn(false);
      setDate(null);
      setTime(null);
      setTz("");
    }
  }, [open]);

  const extractEmailMeta = (meta) => ({
    ...(meta?.to_addresses && { to_addresses: meta.to_addresses }),
    ...(meta?.cc_addresses && { cc_addresses: meta.cc_addresses }),
    ...(meta?.bcc_addresses && { bcc_addresses: meta.bcc_addresses }),
    ...(meta?.reply_address && { reply_address: meta.reply_address }),
    ...(meta?.from_address && { from_address: meta.from_address }),
    ...(meta?.relationships && { relationships: meta.relationships }),
  });

  const handleSend = () => {
    const name =
      selectedTalent?.name ||
      `${selectedTalent?.first_name || ""} ${selectedTalent?.last_name || ""}`.trim();
    
    const combinedHtml = `
      ${html}
      ${htmlFooter}
    `;

    const cleanedSubject = subject
      ?.replace(/^AutoGenerated Email:\s*/i, "")
      ?.replace(/^Subject line:\s*/i, "")
      ?.trim();

    const invitePayload = {
      "message_data": [
        {
          "subject": cleanedSubject,
          "static_message_html": combinedHtml,
          ...extractEmailMeta(emailMeta),
        }
      ]
    }

    if (scheduleOn && date && time && tz) {
      const fireAfter = dayjs(date)
        .hour(time.hour())
        .minute(time.minute())
        .second(0)
        .format("YYYY-MM-DDTHH:mm:ss");

      invitePayload.fire_after = fireAfter;
      invitePayload.fire_after_tz_name = tz;
    }

    const inviteData = {
      "event_shift2role2person_uuid": selectedRolePersonUUID,
      "email_job": invitePayload
    }

    const payload = {
      inviteData,
      onSuccess: async () => {
        const payload = {
          "uuid": selectedRolePersonUUID,
          "status": inviteType
        }
        await triggerUpdateShortlistedStatus(payload)
        onClose();
        toast.success(`Successfully sent the invite to ${name}`);
      },
      onError: () => {
        console.error('Error while sending ')
      }
    }

    triggerSendInvite(payload)
  };

  const handleClose = () => {
    setSubject("");
    setHtml("");
    setScheduleOn(false);
    setDate(null);
    setTime(null);
    setTz("UTC+00:00");
    onClose();
  }

  const btnName = scheduleOn ? "Schedule Invite" : inviteBtnName

  const onChangeTimezone = (value) => {
    setTz(value);
  };
 
  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={700}
      centered
      closable={false}
    >
      <div className="rounded-lg overflow-hidden">
        <div className="flex items-center justify-between font-medium text-2xl mb-4">
          <h1>Edit Template</h1>
          <CloseOutlined onClick={handleClose} style={{ fontSize: 18, cursor: "pointer" }} />
        </div>

        <div>
          <div className="mb-4">
            <Input
              className="!border !border-grayBorder focus:!border-grayBorder hover:!border-grayBorder focus:!shadow-none"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject Line: Shortlisted/Invited"
            />
          </div>

          <RichTextEditor key={selectedTalent?.uuid} initialHtml={html} onChangeHtml={setHtml} className="mb-4" />

          <div className="mt-4 pt-6 pb-9">
            <div className="flex items-center justify-end gap-2 mb-6">
              <div className="font-semibold">Schedule</div>
              <Switch checked={scheduleOn} onChange={setScheduleOn} />
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <ScheduleDatePicker
                  disabled={!scheduleOn}
                  value={date}
                  onChange={(d) => setDate(d ? dayjs(d) : null)}
                />
              </Col>

              <Col xs={24} sm={8}>
                <ScheduleTimePicker
                  disabled={!scheduleOn}
                  value={time}
                  onChange={(t) => setTime(t ? dayjs(t) : null)}
                />
              </Col>

              <Col xs={24} sm={8}>
                <ScheduleTimezonePicker
                  disabled={!scheduleOn}
                  value={tz}
                  onChange={onChangeTimezone}
                />

              </Col>
            </Row>
          </div>

          <div className="flex items-center justify-end mt-6">
            <div className="flex gap-3 rounded-lg">
              <Button type="primary" onClick={handleSend}>
                {btnName}
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  shortlisted: state.tiers.shortlisted || []
});

const mapDispatchToProps = {
  triggerSendInvite: sendInvite,
  triggerUpdateShortlistedStatus: updateShortlistedStatus
}

export default connect(mapStateToProps, mapDispatchToProps)(RichTextEditorModal);
